import { useEffect, useRef, useState, useCallback } from "react"
import { cn } from "@vc/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu"
import { ChevronDown, Globe, Loader2, GripVertical } from "lucide-react"

// Supported languages for translation
const LANGUAGES = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "hi", name: "Hindi", flag: "🇮🇳" },
  { code: "ta", name: "Tamil", flag: "🇮🇳" },
  { code: "te", name: "Telugu", flag: "🇮🇳" },
  { code: "kn", name: "Kannada", flag: "🇮🇳" },
  { code: "ml", name: "Malayalam", flag: "🇮🇳" },
]

// Translation function using MyMemory API (free tier)
async function translateText(text: string, targetLang: string): Promise<string> {
  if (!text.trim()) return text
  
  // Don't translate if already in English and target is English
  if (targetLang === "en") return text
  
  try {
    const response = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
    )
    const data = await response.json()
    
    if (data.responseStatus === 200 && data.responseData?.translatedText) {
      return data.responseData.translatedText
    }
    
    // Fallback: return original text if translation fails
    console.warn("Translation failed, returning original text")
    return text
  } catch (error) {
    console.error("Translation error:", error)
    return text // Return original on error
  }
}

interface ExternalCaptionEntry {
  id: string
  speaker: string
  text: string
  ts: number
}

interface CaptionsOverlayProps {
  isActive: boolean
  participantName?: string
  /**
   * Server-sourced, multi-peer captions (see useLiveCaptions). When provided
   * (even as an empty array), this overlay renders from it directly instead
   * of running the browser's own Web Speech API — which only ever hears the
   * local participant and has no server-side record. `undefined` keeps the
   * original local-only behavior for a meeting that hasn't enabled server
   * transcription.
   */
  externalEntries?: ExternalCaptionEntry[]
}

// TypeScript types for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  isFinal: boolean
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message: string
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
  start(): void
  stop(): void
  abort(): void
}

// Extend Window interface for SpeechRecognition
declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition
    webkitSpeechRecognition?: new () => SpeechRecognition
  }
}

interface CaptionEntry {
  id: string
  text: string
  isFinal: boolean
  timestamp: number
  /** Only set when sourced from externalEntries — a specific peer's name. */
  speaker?: string
}

export function CaptionsOverlay({ isActive, participantName = "You", externalEntries }: CaptionsOverlayProps) {
  const isExternal = externalEntries !== undefined
  const [captions, setCaptions] = useState<CaptionEntry[]>([])
  const [isListening, setIsListening] = useState(false)
  const [isTranslating, setIsTranslating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES[0])
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const silenceTimeoutRef = useRef<number | null>(null)
  
  // Drag state
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragOffsetRef = useRef({ x: 0, y: 0 })
  const wrapperRef = useRef<HTMLDivElement>(null)
  
  // Calculate default center-bottom position
  const getDefaultPosition = useCallback(() => {
    if (!wrapperRef.current) return { x: 0, y: 0 }
    const viewportW = window.innerWidth
    const elementW = wrapperRef.current.offsetWidth
    // Center horizontally, position near bottom
    return {
      x: Math.max(0, (viewportW - elementW) / 2),
      y: 0 // y: 0 means bottom positioning via CSS
    }
  }, [])
  
  // Set default position on mount
  useEffect(() => {
    if (position === null && isActive && wrapperRef.current) {
      setPosition(getDefaultPosition())
    }
  }, [position, isActive, getDefaultPosition])
  
  // Recalculate on window resize (only if not dragged)
  useEffect(() => {
    const handleResize = () => {
      if (!isDragging && wrapperRef.current) {
        setPosition(getDefaultPosition())
      }
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [isDragging, getDefaultPosition])

  // Get SpeechRecognition constructor
  const getSpeechRecognition = useCallback((): SpeechRecognition | null => {
    if (typeof window === "undefined") return null
    const SpeechRecognitionConstructor = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognitionConstructor) return null
    return new SpeechRecognitionConstructor()
  }, [])

  // Server-sourced captions: mirror externalEntries into the same state the
  // renderer already reads, and skip the browser's own SpeechRecognition
  // entirely (the next effect below bails out via isExternal).
  useEffect(() => {
    if (!isExternal) return
    if (!isActive || !externalEntries) {
      setCaptions([])
      setIsListening(false)
      return
    }
    setIsListening(true)
    setError(null)
    setCaptions(
      externalEntries.map((e) => ({
        id: e.id,
        text: e.text,
        isFinal: true,
        timestamp: e.ts,
        speaker: e.speaker,
      })),
    )
  }, [isExternal, isActive, externalEntries])

  // Initialize speech recognition — local-only fallback for a meeting that
  // has not enabled server transcription (see externalEntries above).
  useEffect(() => {
    if (isExternal) return
    if (!isActive) {
      // Clear silence timeout
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current)
        silenceTimeoutRef.current = null
      }
      setCaptions([])
      setIsListening(false)
      setError(null)
      if (recognitionRef.current) {
        recognitionRef.current.stop()
        recognitionRef.current = null
      }
      return
    }

    // Clear captions when starting fresh
    setCaptions([])
    setError(null)

    const recognition = getSpeechRecognition()
    if (!recognition) {
      setError("Speech recognition not supported in this browser")
      return
    }

    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = "en-US" // Always recognize in English, then translate
    recognition.maxAlternatives = 1

    recognition.onstart = () => {
      setIsListening(true)
      setError(null)
    }

    recognition.onend = () => {
      setIsListening(false)
      // Restart if still active with a small delay
      if (isActive) {
        setTimeout(() => {
          if (isActive && recognitionRef.current) {
            try {
              recognitionRef.current.start()
            } catch {
              // Try creating a new instance if restart fails
              setTimeout(() => {
                if (isActive) {
                  const newRecognition = getSpeechRecognition()
                  if (newRecognition) {
                    recognitionRef.current = newRecognition
                    try {
                      newRecognition.start()
                    } catch {
                      // Failed to start new instance
                    }
                  }
                }
              }, 500)
            }
          }
        }, 100)
      }
    }

    recognition.onerror = (event) => {
      if (event.error === "no-speech") {
        // Ignore no-speech errors, they're normal
        return
      }
      if (event.error === "aborted") {
        // Ignore aborted errors
        return
      }
      if (event.error === "network") {
        // Network error - try to restart
        setError("Network error, restarting...")
        setTimeout(() => {
          if (isActive && recognitionRef.current) {
            try {
              recognitionRef.current.start()
              setError(null)
            } catch {
              // Failed to restart
            }
          }
        }, 1000)
        return
      }
      setError(`Speech recognition error: ${event.error}`)
      setIsListening(false)
      
      // Try to restart after 2 seconds
      setTimeout(() => {
        if (isActive && recognitionRef.current) {
          try {
            recognitionRef.current.start()
            setError(null)
          } catch {
            // Failed to restart
          }
        }
      }, 2000)
    }

    recognition.onresult = async (event) => {
      const results = event.results
      let finalTranscript = ""
      let interimTranscript = ""

      for (let i = event.resultIndex; i < results.length; i++) {
        const result = results[i]
        const transcript = result[0].transcript
        if (result.isFinal) {
          finalTranscript += transcript
        } else {
          interimTranscript += transcript
        }
      }

      // Clear existing silence timeout and set new one
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current)
      }

      // Translate interim transcript if needed
      if (interimTranscript.trim() && selectedLanguage.code !== "en") {
        setIsTranslating(true)
        try {
          interimTranscript = await translateText(interimTranscript, selectedLanguage.code)
        } catch (err) {
          console.error("Translation error:", err)
        } finally {
          setIsTranslating(false)
        }
      }

      // Translate final transcript if needed
      if (finalTranscript.trim() && selectedLanguage.code !== "en") {
        setIsTranslating(true)
        try {
          finalTranscript = await translateText(finalTranscript, selectedLanguage.code)
        } catch (err) {
          console.error("Translation error:", err)
        } finally {
          setIsTranslating(false)
        }
      }

      setCaptions((prev) => {
        const newCaptions = [...prev]
        
        // Remove previous interim caption if exists
        const lastIndex = newCaptions.length - 1
        if (lastIndex >= 0 && !newCaptions[lastIndex].isFinal) {
          newCaptions.pop()
        }

        // Add interim caption if exists
        if (interimTranscript.trim()) {
          newCaptions.push({
            id: `interim-${Date.now()}`,
            text: interimTranscript,
            isFinal: false,
            timestamp: Date.now(),
          })
        }

        // Add final caption if exists
        if (finalTranscript.trim()) {
          newCaptions.push({
            id: `final-${Date.now()}`,
            text: finalTranscript,
            isFinal: true,
            timestamp: Date.now(),
          })
        }

        // Keep only last 3 captions to avoid overcrowding
        const finalCaptions = newCaptions.slice(-3)
        return finalCaptions
      })

      // Set timeout to clear captions after 3 seconds of silence
      silenceTimeoutRef.current = window.setTimeout(() => {
        setCaptions([])
      }, 3000)
    }

    recognitionRef.current = recognition

    try {
      recognition.start()
    } catch (err) {
      console.error("Failed to start speech recognition:", err)
      setError("Failed to start speech recognition")
    }

    return () => {
      try {
        recognition.stop()
      } catch {
        // Ignore stop errors
      }
      recognitionRef.current = null
    }
  }, [isActive, isExternal, getSpeechRecognition, selectedLanguage.code])

  // Auto-scroll to bottom
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [captions])

  // Drag handlers
  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    if (!wrapperRef.current) return
    const rect = wrapperRef.current.getBoundingClientRect()
    dragOffsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    }
    setIsDragging(true)
  }, [])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    handleDragStart(e.clientX, e.clientY)
  }, [handleDragStart])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    handleDragStart(touch.clientX, touch.clientY)
  }, [handleDragStart])

  const handleDragMove = useCallback((clientX: number, clientY: number) => {
    if (!isDragging) return
    
    const newX = clientX - dragOffsetRef.current.x
    const newY = clientY - dragOffsetRef.current.y
    
    // Get viewport dimensions
    const viewportW = window.innerWidth
    const viewportH = window.innerHeight
    
    // Get element dimensions (approximate since we don't have ref during move)
    const elementW = wrapperRef.current?.offsetWidth || 400
    const elementH = wrapperRef.current?.offsetHeight || 150
    
    // Constrain to viewport bounds
    const constrainedX = Math.max(0, Math.min(newX, viewportW - elementW))
    const constrainedY = Math.max(0, Math.min(newY, viewportH - elementH))
    
    setPosition({ x: constrainedX, y: constrainedY })
  }, [isDragging])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    handleDragMove(e.clientX, e.clientY)
  }, [handleDragMove])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    const touch = e.touches[0]
    handleDragMove(touch.clientX, touch.clientY)
  }, [handleDragMove])

  const handleDragEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Attach global drag listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleDragEnd)
      window.addEventListener("touchmove", handleTouchMove)
      window.addEventListener("touchend", handleDragEnd)
      
      return () => {
        window.removeEventListener("mousemove", handleMouseMove)
        window.removeEventListener("mouseup", handleDragEnd)
        window.removeEventListener("touchmove", handleTouchMove)
        window.removeEventListener("touchend", handleDragEnd)
      }
    }
  }, [isDragging, handleMouseMove, handleTouchMove, handleDragEnd])

  // Stays visible the whole time captions are on, even with nothing said yet
  // — falls through to the "Start speaking..." placeholder below instead of
  // disappearing, so turning CC on has visible effect immediately rather than
  // only once someone speaks. Off is still fully hidden: nothing to drag,
  // nothing taking up space, for a viewer who doesn't want captions.
  if (!isActive) return null

  return (
    <div
      ref={wrapperRef}
      className={cn(
        "fixed z-40",
        isDragging ? "cursor-grabbing select-none" : "cursor-grab",
        "transition-shadow duration-200",
        isDragging && "shadow-2xl ring-2 ring-white/20"
      )}
      style={{
        left: position?.x ?? "50%",
        top: position?.y ?? "auto",
        bottom: position?.y === undefined || position?.y === 0 ? "6rem" : "auto",
        transform: position === null ? "translateX(-50%)" : undefined,
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div
        ref={containerRef}
        className={cn(
          "max-w-3xl mx-4 rounded-2xl px-6 py-4 transition-all duration-300 pointer-events-auto",
          "bg-black/40 backdrop-blur-md border border-white/10",
          "shadow-2xl"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <GripVertical className="h-4 w-4 text-white/40" />
            <div
              className={cn(
                "h-2 w-2 rounded-full transition-colors duration-300",
                isListening ? "bg-green-400 animate-pulse" : "bg-amber-400"
              )}
            />
            {/* Each line carries its own speaker when server-sourced — a
                single fixed name here would be misleading. */}
            {!isExternal && participantName && (
              <span className="text-xs text-white/50">{participantName}</span>
            )}
            {isExternal && (
              <span className="text-xs text-white/50">Live Transcription</span>
            )}
          </div>
          
          {/* Language Selector Dropdown — translation only runs on the local
              Web Speech path today; hidden here rather than shown and
              silently doing nothing for server-sourced captions. */}
          {!isExternal && (
          <div className="flex items-center gap-2">
            {isTranslating && (
              <div className="flex items-center gap-1 text-xs text-white/50">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Translating...</span>
              </div>
            )}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button 
                  type="button"
                  className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-card/10 transition-colors text-xs text-white/70 pointer-events-auto"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <Globe className="h-3 w-3" />
                  <span>{selectedLanguage.flag} {selectedLanguage.name}</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[140px] bg-[#1f2937] border-white/10 pointer-events-auto">
              {LANGUAGES.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onSelect={() => {
                    setSelectedLanguage(lang)
                  }}
                  className={cn(
                    "flex items-center gap-2 cursor-pointer text-white/90 hover:bg-card/10 pointer-events-auto",
                    selectedLanguage.code === lang.code && "bg-card/10"
                  )}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
          )}
        </div>

        {/* Captions text */}
        <div className="space-y-1">
          {captions.length === 0 && !error && (
            <p className="text-white/40 text-sm italic">
              Start speaking to see captions...
            </p>
          )}
          
          {error && (
            <p className="text-red-300 text-sm">
              {error}
            </p>
          )}

          {captions.map((caption, index) => (
            <p
              key={caption.id}
              className={cn(
                "text-lg leading-relaxed transition-all duration-200",
                caption.isFinal
                  ? "text-white font-medium"
                  : "text-white/60 italic",
                index === captions.length - 1 && "animate-in fade-in slide-in-from-bottom-2 duration-300"
              )}
            >
              {/* Only set for server-sourced, multi-peer captions — the
                  local-only path has a single fixed participantName in the
                  header instead, same as before. */}
              {caption.speaker && (
                <span className="text-white/50 font-semibold mr-2">{caption.speaker}:</span>
              )}
              {caption.text}
            </p>
          ))}
        </div>

        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-black/50 to-transparent rounded-b-2xl pointer-events-none" />
      </div>
    </div>
  )
}
